import { updateTrackingFields, UserProfile } from 'api/profiles'
import { useAuth } from 'contexts'
import dayjs from 'dayjs'
import { useUserProfile } from 'hooks/useUserProfile'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'

export interface UtmFields {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  landing_page?: string
  captured_at?: string
}

const LOCAL_STORAGE_KEY = 'fairart_utm'

function toString(input: unknown) {
  if (typeof input !== 'string') return undefined
  return input
}

function isBrowser() {
  return typeof window !== 'undefined'
}

function writeUtms(utm: UtmFields): void {
  if (!isBrowser()) return
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(utm))
  } catch {
    /** empty */
  }
}

function readUtms(): UtmFields | null {
  if (!isBrowser()) return null
  try {
    const value = window.localStorage.getItem(LOCAL_STORAGE_KEY)
    if (!value) return null
    return JSON.parse(value)
  } catch {
    return null
  }
}

export function deleteUtms() {
  if (!isBrowser()) return
  try {
    window.localStorage.removeItem(LOCAL_STORAGE_KEY)
  } catch {
    /** empty */
  }
}

function hasAnyUtm(utm: UtmFields): boolean {
  return Boolean(
    utm.utm_campaign || utm.utm_content || utm.utm_medium || utm.utm_source
  )
}

function shouldOverwiteUtm(saved: UtmFields | null, current: UtmFields) {
  if (!saved) return true // if no utms saved yet, write them to localstorage for the first time
  if (hasAnyUtm(saved)) return false // if we have utms saved, avoid overwriting
  return hasAnyUtm(current) // if we have something saved, but not utm fields (only landing page, etc), save only if the current version has utm fields
}

function userHasAnyUtm(userProfile: UserProfile) {
  return Boolean(
    userProfile.utmCampaign ||
      userProfile.utmContent ||
      userProfile.utmMedium ||
      userProfile.utmSource ||
      userProfile.utmLandingPage
  )
}

export function useTrackUtms() {
  const router = useRouter()
  const { user, isLoading: isLoadingAuth } = useAuth()
  const { data: userProfile, isFetching: isFetchingUserProfile } =
    useUserProfile()
  const [utm, setUtm] = useState<UtmFields | null>(null)

  const isSettingUtm = useRef(false)

  useEffect(() => {
    function determineUtm() {
      if (isLoadingAuth) return

      const savedUtm = readUtms()

      const utm: UtmFields = {
        utm_campaign: toString(router.query.utm_campaign),
        captured_at: new Date().toISOString(),
        utm_content: toString(router.query.utm_content),
        landing_page: router.asPath.split('?')[0],
        utm_medium: toString(router.query.utm_medium),
        utm_source: toString(router.query.utm_source),
      }

      // avoid overwriting the local storage values
      if (!shouldOverwiteUtm(savedUtm, utm)) {
        setUtm(savedUtm)
        return
      }

      writeUtms(utm)
      return setUtm(utm)
    }

    determineUtm()
  }, [router, isLoadingAuth])

  useEffect(() => {
    function saveUser() {
      if (
        !user ||
        !userProfile ||
        !utm ||
        userHasAnyUtm(userProfile) ||
        isFetchingUserProfile ||
        isSettingUtm.current
      )
        return

      const currentDate = dayjs()
      const userCreatedAt = dayjs(user.createdAt)
      const diff = currentDate.diff(userCreatedAt, 'm')

      if (diff > 1) return // avoid updating an user created longer than 1 minute ago

      isSettingUtm.current = true

      updateTrackingFields(user.id, {
        utmCampaign: utm.utm_campaign,
        utmContent: utm.utm_content,
        utmLandingPage: utm.landing_page,
        utmMedium: utm.utm_medium,
        utmSource: utm.utm_source,
      }).catch(() => {
        isSettingUtm.current = false
      })
    }

    saveUser()
  }, [user, userProfile, isFetchingUserProfile, utm])
}
